import { createSelector } from 'reselect';
import {CarSpeedDataframeDto, CarAccelerationDataframeDto,CarSpeedDataframeDtoWithDiff,SpeedDataTimeGroup,ActiveSpeedDataTimeGroup} from '../../Models/CollisionModel'
import { RootState } from 'Redux/Reducers/_RootReducer';
import moment from 'moment';
import { List } from 'immutable';
import { LatLngExpression } from 'leaflet';

export class HistoryCollisionSelectors {

    private static toDegrees (angle) {
        return angle * (180 / Math.PI);
    }
    
    private static getElementById(elementList:List<any>,idElement:number) {
        if(elementList.size>0 && idElement!=null) {
            const aciveEl:any = elementList.toArray().find((value) => {
                return value.id == idElement
            });
            return (aciveEl!=-1)?aciveEl:null;
        }

        return null;
    }

    public static getDataframesAcceleration = createSelector(        
        (state:RootState) => state.historyCollision.dataframesAcceleration,
        (dataframesAcceleration:List<CarAccelerationDataframeDto>)=> {
            return dataframesAcceleration.toArray();
        }
    )

    public static getRidePath = createSelector(        
        (state:RootState) => state.historyCollision.dataframesSpeed,
        (dataframesAcceleration:List<CarSpeedDataframeDto>)=> {
            return dataframesAcceleration.toArray().map((el):LatLngExpression=> {
                return [el.latitude, el.longitude]
            })
        }
    )

    /**
     * Selektor na podstawie listy instalaccji i id aktywnego zwraca obiekt aktywnego zlecenia montażu
     *
     * @static
     * @memberof TerminalInstallationSelectors
     */
    public static getActiveCollision = createSelector(        
        (state:RootState) => state.historyCollision.collisionList,
        (state:RootState) => state.historyCollision.activeCollision,
        HistoryCollisionSelectors.getElementById
    )

    /**
     * Selektor na podstawie listy ramek zwraca vektor przyspieszen (obroty wektora podstawowego)
     *
     * @static
     * @memberof HistoryCollisionSelectors
     */
    public static getActiveAccelerationVectorRotate = createSelector(        
        (state:RootState) => HistoryCollisionSelectors.getActiveAccelerationDataframe(state),
        (dataframeAcceleration:CarAccelerationDataframeDto)=> {

            if(dataframeAcceleration) {
                const point = {
                    x:dataframeAcceleration.accelerationX,
                    y:dataframeAcceleration.accelerationY,
                    z:dataframeAcceleration.accelerationZ
                }
    
                const alfa = Math.acos(point.x / dataframeAcceleration.acceleration);
                const beta = Math.acos(point.z / dataframeAcceleration.acceleration);
                const gamma = Math.acos(point.y / dataframeAcceleration.acceleration);
    
                return {
                    alfa:HistoryCollisionSelectors.toDegrees(alfa),
                    beta:HistoryCollisionSelectors.toDegrees(beta),
                    gamma:HistoryCollisionSelectors.toDegrees(gamma),
                    length:dataframeAcceleration.acceleration
                }
            }

            return {
                alfa:0,
                beta:0,
                gamma:0,
                length:0
            }
        }
    )

    /**
     * Selektor na podstawie listy ramek zwraca aktualną ramke dla przyspieszeń
     *
     * @static
     * @memberof HistoryCollisionSelectors
     */
    public static getActiveAccelerationDataframe = createSelector(        
        (state:RootState) => state.historyCollision.dataframesAcceleration,
        (state:RootState) => state.historyCollision.activeDataframes,
        (dataframesAcceleration:List<CarAccelerationDataframeDto>,activeDataframes:string)=> {
            const dataframeAcceleration = dataframesAcceleration.toArray().find((el)=> {
                return el.dateTime == activeDataframes;
            })

            if(dataframeAcceleration) {
                return dataframeAcceleration;
            }

            return null;
        }
    )

    /**
     * Selektor na podstawie listy ramek zwraca aktualną ramke dla prędkosci
     *
     * @static
     * @memberof HistoryCollisionSelectors
     */
    public static getActiveSpeedDataframe = createSelector(        
        (state:RootState) => state.historyCollision.dataframesSpeed,
        (state:RootState) => state.historyCollision.activeDataframes,
        (dataframesAcceleration:List<CarSpeedDataframeDto>,activeDataframes:string)=> {
            const dataframeSpeed = dataframesAcceleration.toArray().find((el)=> {
                return el.dateTime == activeDataframes;
            })

            if(dataframeSpeed) {
                return dataframeSpeed;
            }

            return null;
        }
    )

    /**
     * Selektor na podstawie aktualnej ramki dla prędkości zwraca ramke wzbogaconą o dateTimeDiff
     *
     * @static
     * @memberof HistoryCollisionSelectors
     */
    public static getActiveSpeedDataframeWithDiff = createSelector(        
        (state:RootState) => HistoryCollisionSelectors.getActiveSpeedDataframe(state),
        (state:RootState) => state.historyCollision.crashMoment,
        (activeSpeedDataframe:CarSpeedDataframeDto,crashMoment:moment.Moment):CarSpeedDataframeDtoWithDiff=> {
            if(!activeSpeedDataframe) {
                return null;
            }
            
            return {
                ...activeSpeedDataframe,
                dateTimeDiff:HistoryCollisionSelectors.getDiffTime(moment(activeSpeedDataframe.dateTime),crashMoment)
            }
        }
    )

    /**
     * Metoda pomocnicza do obliczania różnicy czasu (zwraca wynik w sekundach)
     *
     */
    private static getDiffTime(time:moment.Moment,timePoint:moment.Moment) {
        const diff = moment(moment(time).diff(timePoint)).valueOf();

        return diff/1000;
    }

    /**
     * Selektor na podstawie listy ramek predkości i momentu wypadku zwraca liste ramek predkości wzbogaconą o dateTimeDiff
     *
     * @static
     * @memberof HistoryCollisionSelectors
     */
    public static getSpeedDataWithDiff = createSelector(        
        (state:RootState) => state.historyCollision.dataframesSpeed,
        (state:RootState) => state.historyCollision.crashMoment,
        (dataframesSpeed:List<CarSpeedDataframeDto>,crashMoment:moment.Moment):Array<CarSpeedDataframeDtoWithDiff> => {
            const dataframesSpeedTemp = dataframesSpeed.toArray().map((el:CarSpeedDataframeDto,i)=> {
                return {...el,dateTimeDiff:HistoryCollisionSelectors.getDiffTime(moment(el.dateTime),crashMoment)}                                      
            })

            return dataframesSpeedTemp;
        }
    )


    /**
     * Selektor na podstawie listy ramek predkości zwraca listey ramek predkości zgrupowane względem czasu
     *
     * @static
     * @memberof HistoryCollisionSelectors
     */
    public static getSpeedDataTimeGroup = createSelector(        
        (state:RootState) => HistoryCollisionSelectors.getSpeedDataWithDiff(state),
        (dataframesSpeed:Array<CarSpeedDataframeDtoWithDiff>):Array<SpeedDataTimeGroup> => {

            const speedDataTimeGroup:Array<SpeedDataTimeGroup> = new Array();

            dataframesSpeed.forEach((elDataframe:CarSpeedDataframeDtoWithDiff,i)=> {
                
                let indexSpeedDataTimeGroup = speedDataTimeGroup.findIndex((element:SpeedDataTimeGroup) => {
                    return element.dateTimeDiff == Math.floor(elDataframe.dateTimeDiff);
                })

                if(indexSpeedDataTimeGroup == -1) {
                    speedDataTimeGroup.push({
                        dateTimeDiff:Math.floor(elDataframe.dateTimeDiff),
                        dataframes:new Array()
                    })

                    indexSpeedDataTimeGroup = speedDataTimeGroup.length - 1;
                } 
                
                speedDataTimeGroup[indexSpeedDataTimeGroup].dataframes.push(elDataframe);
            })

            return speedDataTimeGroup;
        }
    )

    /**
     * Selektor na podstawie listy ramek predkości zwraca listey ramek predkości zgrupowane względem czasu
     *
     * @static
     * @memberof HistoryCollisionSelectors
     */
    public static getActiveSpeedDataTimeGroup = createSelector(        
        (state:RootState) => HistoryCollisionSelectors.getSpeedDataTimeGroup(state),
        (state:RootState) => state.historyCollision.activeDataframes,
        (speedDataTimeGroup:Array<SpeedDataTimeGroup>,activeDataframes:string):ActiveSpeedDataTimeGroup => {
            let activeGroupIndex = 0;
            let activeElementInGroupIndex = 0;
            let activeElementInGroupIndexTemp = 0;

            activeGroupIndex = speedDataTimeGroup.findIndex((speedDataTimeGroupEl) => {
                activeElementInGroupIndexTemp = speedDataTimeGroupEl.dataframes.findIndex((dataframesEl) => {
                    return  activeDataframes == dataframesEl.dateTime
                });

                if(activeElementInGroupIndexTemp !=-1) {
                    activeElementInGroupIndex = activeElementInGroupIndexTemp;
                    return true;
                }

                return false;
            })

            return {
                activeGroupIndex,
                activeElementInGroupIndex
            };
        }
    )

    /**
     * Selektor na podstawie listy ramek zwraca dla obiekt {speeds,times,accelerations} dla wykresu
     *
     * @static
     * @memberof HistoryCollisionSelectors
     */
    public static getSpeedDataForCharts = createSelector(        
        (state:RootState) => HistoryCollisionSelectors.getSpeedDataWithDiff(state),
        (dataframesSpeed:Array<CarSpeedDataframeDtoWithDiff>)=> {

            const speedsTemp = new Array();
            const timesTemp = new Array();
            const accelerationsTemp = new Array();

            if(dataframesSpeed.length>0) {
                dataframesSpeed.forEach((el:CarSpeedDataframeDtoWithDiff,i)=> {
                    if(i<1000) {

                        speedsTemp.push({
                            dateTime:el.dateTime,
                            y:el.speedGps,
                            x:el.dateTimeDiff
                        });

                        accelerationsTemp.push({
                            dateTime:el.dateTime,
                            y:Math.round(el.acceleration * 100) / 100,
                            x:el.dateTimeDiff
                        });

                        //timesTemp.push(moment(el.dateTime).valueOf());
                        timesTemp.push(el.dateTime); 
                    }                                 
                })
            }

            return {
                speeds:speedsTemp,
                times:timesTemp,
                accelerations:accelerationsTemp
            };
        }
    )
}



// WEBPACK FOOTER //
// ./src/Redux/Selectors/CollisionSelectors.ts